import {
  types,
  applySnapshot,
  getSnapshot,
  IStateTreeNode,
} from 'mobx-state-tree'
import {AuthState} from '$src/auth/types'
import {FieldValuesStore} from '$src/common/forms'
import {v4 as uuid} from 'node-uuid'
import {mapValues} from 'lodash'
import decodeJwtToken from 'jwt-decode'
import moment from 'moment'
import HousingCorporationsListPage from '$src/housingCorporations/HousingCorporationsListPage'

const GeneralStore = types
  .model('General', {
    bootstrapped: false,
    _mapWidth: types.maybe(types.number),
  })
  .actions(self => {
    return {
      doneBootstrapping() {
        self.bootstrapped = true
      },
      setMapWidth(w: number) {
        self._mapWidth = w
      },
    }
  })
  .views(self => ({
    mapWidth() {
      return typeof self._mapWidth === 'number' &&
        self._mapWidth > 20 &&
        self._mapWidth < 1000
        ? self._mapWidth
        : 400
    },
  }))

const User = types.model('User', {
  name: types.string,
  _id: types.identifier(types.string),
  archived: types.boolean,
  notificationsLastSeen: types.maybe(types.string),
})

const UsersStore = types
  .model('Users', {
    byId: types.map(User),
  })
  .actions(self => ({
    updateNotificationsLastSeen(id: string) {
      const user = self.byId.get(id)
      const date = moment()
        .utc()
        .format()
      const item = {...user, notificationsLastSeen: date}
      self.byId.set(id, item as any)
    },
  }))

const Notification = types.model('Notification', {
  type: types.string,
  _id: types.identifier(types.string),
  title: types.string,
  content: types.string,
  createdAt: types.string,
  updatedAt: types.string,
})

const NotificationStore = types
  .model('Notifications', {
    byId: types.map(Notification),
    list: types.array(types.string),
    status: types.union(
      types.literal('NotLoaded'),
      types.literal('Loading'),
      types.literal('Loaded'),
      types.literal('Failed'),
    ),
  })
  .actions(self => ({
    setStatus(status: 'NotLoaded' | 'Loading' | 'Loaded' | 'Failed') {
      self.status = status
    },
  }))
  .views(self => ({
    getAlert() {
      return self.byId
        .entries()
        .filter(([_, n]) => n && n.type === 'alert')
        .map(([_, n]) => ({...n, content: n.content.split('\n')}))
    },
    getNormal() {
      return self.byId
        .entries()
        .filter(([_, n]) => n && n.type === 'normal')
        .map(([_, n]) => ({...n, content: n.content.split('\n')}))
    },
  }))

const AuthStore = types
  .model('Auth', {
    __state: types.union(
      types.model({isLoggedIn: types.literal(false)}),
      types.model({isLoggedIn: types.literal(true), jwt: types.string}),
    ),
  })
  .actions(self => {
    return {
      afterCreate() {
        const possibleAuthStateString = localStorage.getItem(
          process.env.REACT_APP_AUTH_LOCALSTORAGE_KEY,
        )
        if (
          typeof possibleAuthStateString === 'string' &&
          possibleAuthStateString.length > 0
        ) {
          try {
            const __state = JSON.parse(possibleAuthStateString)
            applySnapshot(self, {__state})
          } catch (e) {
            applySnapshot(self, {__state: {isLoggedIn: false}})
          }
        }
      },
      __setState(__state: AuthState) {
        localStorage.setItem(
          process.env.REACT_APP_AUTH_LOCALSTORAGE_KEY,
          JSON.stringify(__state),
        )
        applySnapshot(self, {__state})
      },
    }
  })
  .actions(self => ({
    setAsLoggedIn(p: {jwt: string}) {
      self.__setState({isLoggedIn: true, jwt: p.jwt})
    },
    setAsLoggedOut() {
      self.__setState({isLoggedIn: false})
    },
  }))
  .views(self => ({
    getState(): AuthState {
      return getSnapshot(self.__state)
    },
    getMyId() {
      const state = getSnapshot(self.__state)
      if (state.isLoggedIn) {
        return decodeJwtToken<{user: {_id: string}}>(state.jwt).user._id
      } else {
        return undefined
      }
    },
  }))

const BaseProject = types.model('BaseProject', {
  _id: types.identifier(types.number),
  name: types.string,
  numberOfApartments: types.number,
  evaluationDate: types.maybe(types.string),
  creationDate: types.string,
  housingCorporations: types.array(types.string),
  archived: types.boolean,
  customer: types.maybe(types.string),
  usersInvolved: types.array(types.string),
})

const ProjectsStore = types
  .model('Projects', {
    baseProjectsById: types.map(BaseProject),
    listOfProjectsInProjectsPage: types.array(types.reference(BaseProject)),
  })
  .actions(self => {
    return {
      import(ids: {[id: number]: typeof BaseProject.Type}) {
        // applySnapshot(self.baseProjectsById, {...getSnapshot(self.baseProjectsById), ...ids})
        self.baseProjectsById.merge(ids)
        // self.baseProjectsById =
      },
    }
  })

const Location = types.model('Location', {
  lat: types.number,
  long: types.number,
})

export const HousingCorporation = types
  .model('HousingCorporation', {
    _id: types.identifier(types.string),
    name: types.string,
    address: types.string,
    comment: types.maybe(types.string),
    municipality: types.string,
    location: Location,
    parentId: types.string,
    __v: types.number,
    /**
     * This is now being used as the project's "Frozen" indicator.
     * I tried to put the "frozen" state inside ResearchState, but
     * had to undo it because the extra complexity was unnecessary.
     *
     * @todo We should rename the value itself at some point to "frozen".
     */
    ready: types.boolean,
    summaryAdjusted: types.boolean,
    hidden: types.boolean,
  })
  .views(self => ({
    isFrozen() {
      return self.ready === true
    },
    isSummaryAdjusted() {
      return self.summaryAdjusted === true
    },
    isHidden() {
      return self.hidden === true
    },
  }))
  .actions(self => ({
    freeze() {
      self.ready = true
    },
    thaw() {
      self.ready = false
    },
    toggleSummary() {
      self.summaryAdjusted = !self.summaryAdjusted
    },
    setComment(c: string) {
      self.comment = c
    },
    toggleHidden() {
      self.hidden = !self.hidden
    },
    setAddress(address: string) {
      self.address = address
    },
  }))

/**
 * Housing corporations are always overridden when opening each project,
 * so `byId` and `list` only contain housing corps belonging to a single project.
 *
 * Ideally, we want `list` to be moved to each individual BaseProject, and `byId`
 * to contain housingCorporations from any project. But for now, the current setup
 * is adequate.
 */

interface Data {
  parentId: string
  name: string
  address: string
  municipality: string
  location: {
    lat: number
    long: number
  } & IStateTreeNode
  comment: string | null
  owned: boolean
  constructionDate: Object
  _id: string
  __v: number
  ready: boolean
  summaryAdjusted: boolean
  hidden: boolean
}

const HousingCorporationsStore = types
  .model('HousingCorporations', {
    byId: types.map(HousingCorporation),
    list: types.array(types.reference(HousingCorporation)),
  })
  .actions(self => ({
    add: (data: Data) => {
      try {
        self.byId.set(data._id, data as any)
        self.list.push(data._id as any)
      } catch (e) {
        console.error('ERROR: ', e)
      }
    },
  }))

export const ResearchStateQuery = types.model('ResearchStateQuery', {
  mapShape: types.frozen,
  mapShapeRent: types.frozen,
  formValues: FieldValuesStore,
})

export const ComparableSale = types.model('ComparableSale', {
  id: types.identifier(types.string),
  dataType: types.maybe(types.string),
  location: Location,
  propertyType: types.maybe(types.string),
  municipality: types.maybe(types.string),
  postalCode: types.maybe(types.string),
  streetAddress: types.maybe(types.string),
  livingArea: types.maybe(types.union(types.number, types.string)),
  yearOfConstruction: types.maybe(types.union(types.number, types.string)),
  numberOfRooms: types.maybe(types.union(types.number, types.string)),
  newlyBuilt: types.maybe(types.boolean),
  condition: types.maybe(types.string),
  sellLength: types.maybe(types.union(types.number, types.string)),
  dateOfSale: types.maybe(types.string),
  dateOfRent: types.maybe(types.string),
  plotOwned: types.maybe(types.boolean),
  hasElevator: types.maybe(types.boolean),
  hasSauna: types.maybe(types.boolean),
  apartmentTypeExact: types.maybe(types.string),
  roomsDescription: types.maybe(types.string),
  floorNumber: types.maybe(types.union(types.number, types.string)),
  priceSqmAdjusted: types.maybe(types.union(types.number, types.string)),
  priceFreeOfDeptAdjusted: types.maybe(types.union(types.number, types.string)),
  priceSqm: types.maybe(types.union(types.number, types.string)),
  priceFreeOfDept: types.maybe(types.union(types.number, types.string)),
  owner: types.maybe(types.string),
  source: types.maybe(types.string),
  priceSqmAdjusted1QuarterAgo: types.maybe(types.number),
  priceSqmAdjusted2QuartersAgo: types.maybe(types.number),
  priceSqmAdjusted3QuartersAgo: types.maybe(types.number),
  priceSqmAdjusted4QuartersAgo: types.maybe(types.number),
  rent: types.maybe(types.number),
  rentSqm: types.maybe(types.number),
  accommodationType: types.maybe(types.string),
})

export const ResearchStateTab = types
  .model('ResearchStateTab', {
    name: types.string,
    id: types.identifier(types.string),
    enabled: types.boolean,
    query: ResearchStateQuery,
    foundItems: types.model('ResearchStateTabFoundItems', {
      byId: types.map(ComparableSale),
      list: types.array(types.string),
      queried: types.boolean,
      selectedIds: types.map(types.boolean),
      selectedIdsRent: types.map(types.boolean),
      selectedIdsOldData: types.map(types.boolean),
      selectedPageSize: types.union(types.undefined, types.number),
    }),
  })
  .views(self => ({
    getSelectedPageSize() {
      const recordedPageSize = self.foundItems.selectedPageSize
      return typeof recordedPageSize === 'number'
        ? recordedPageSize
        : self.foundItems.list.length
    },
  }))
  .actions(self => ({
    setName(name: string) {
      self.name = name
    },

    setEnabledStatus(enabled: boolean) {
      self.enabled = enabled
    },

    setMapShape(s: any) {
      self.query.mapShape = s
    },

    setMapShapeRent(s: any) {
      self.query.mapShapeRent = s
    },

    setQueriedStatus(s: boolean) {
      self.foundItems.queried = s
    },

    setSelectedForId(id: string, s: boolean) {
      if (s) {
        self.foundItems.selectedIds.set(id, true)
      } else {
        self.foundItems.selectedIds.delete(id)
      }
    },

    setSelectedForIdRent(id: string, s: boolean) {
      if (s) {
        self.foundItems.selectedIdsRent.set(id, true)
      } else {
        self.foundItems.selectedIdsRent.delete(id)
      }
    },

    setSelectedForIdOldData(id: string, s: boolean) {
      if (s) {
        self.foundItems.selectedIdsOldData.set(id, true)
      } else {
        self.foundItems.selectedIdsOldData.delete(id)
      }
    },

    batchSelectOrUnselectByAddress(originalId: string, shouldSelect: boolean) {
      const streetAddress = self.foundItems.byId.get(originalId)!.streetAddress

      const matches = self.foundItems.byId
        .entries()
        .filter(([_, foundItem]) => foundItem.streetAddress === streetAddress)

      matches.forEach(([_, foundItem]) => {
        if (shouldSelect) {
          self.foundItems.selectedIds.set(foundItem.id, true)
        } else {
          self.foundItems.selectedIds.delete(foundItem.id)
        }
      })
    },

    batchSelectOrUnselectByAddressRent(
      originalId: string,
      shouldSelect: boolean,
    ) {
      const streetAddress = self.foundItems.byId.get(originalId)!.streetAddress

      const matches = self.foundItems.byId
        .entries()
        .filter(([_, foundItem]) => foundItem.streetAddress === streetAddress)

      matches.forEach(([_, foundItem]) => {
        if (shouldSelect) {
          self.foundItems.selectedIdsRent.set(foundItem.id, true)
        } else {
          self.foundItems.selectedIdsRent.delete(foundItem.id)
        }
      })
    },

    batchSelectOrUnselectByAddressOldData(
      originalId: string,
      shouldSelect: boolean,
    ) {
      const streetAddress = self.foundItems.byId.get(originalId)!.streetAddress

      const matches = self.foundItems.byId
        .entries()
        .filter(([_, foundItem]) => foundItem.streetAddress === streetAddress)

      matches.forEach(([_, foundItem]) => {
        if (shouldSelect) {
          self.foundItems.selectedIdsOldData.set(foundItem.id, true)
        } else {
          self.foundItems.selectedIdsOldData.delete(foundItem.id)
        }
      })
    },

    selectAll() {
      const ids = getSnapshot(self.foundItems.byId)
      Object.keys(ids).forEach(id => {
        if (ids[id].dataType === 'premise') {
          self.foundItems.selectedIds.set(id, true)
        }
      })
    },

    deselectAll() {
      self.foundItems.selectedIds.clear()
    },

    selectAllOldData() {
      const ids = getSnapshot(self.foundItems.byId)
      Object.keys(ids).forEach(id => {
        if (!ids[id].dataType) {
          self.foundItems.selectedIdsOldData.set(id, true)
        }
      })
    },

    deselectAllOldData() {
      self.foundItems.selectedIdsOldData.clear()
    },

    selectAllRent() {
      const ids = getSnapshot(self.foundItems.byId)
      Object.keys(ids).forEach(id => {
        if (ids[id].dataType === 'rent') {
          self.foundItems.selectedIdsRent.set(id, true)
        }
      })
    },

    deselectAllRent() {
      self.foundItems.selectedIdsRent.clear()
    },

    setSelectedPageSize(s: undefined | number) {
      self.foundItems.selectedPageSize = s
    },
  }))

export const ResearchStateTabs = types
  .model('ResearchStateTabs', {
    byId: types.map(ResearchStateTab),
    list: types.array(types.reference(ResearchStateTab)),
  })
  .actions(self => ({
    add(item: {id: string; name: string; enabled: boolean}) {
      self.byId.set(item.id, item as any)
      // @ts-ignore
      self.list.push(item.id)
    },
    delete(id: string) {
      const ref = self.byId.get(id)
      self.list.remove(ref!)
      self.byId.delete(id)
    },
  }))

export const ResearchState = types.model('ResearchState', {
  version: types.number,
  tabs: ResearchStateTabs,
  housingCorporationId: types.string,
})

export const ResearchStatesStore = types
  .model('ResearchStates', {
    byHousingCorporationId: types.map(ResearchState),
  })
  .actions(self => ({
    set(id: string, v: typeof ResearchState.Type) {
      self.byHousingCorporationId.set(id, v)
    },
  }))

export const ResearchQueryPreset = types.model('ResearchQueryPreset', {
  _id: types.identifier(types.string),
  name: types.string,
  version: types.literal(1),
  formValues: types.frozen,
})

export const ResearchQueryPresets = types
  .model('ResearchQueryPresets', {
    byId: types.map(ResearchQueryPreset),
    list: types.array(types.string),
    status: types.union(
      types.literal('NotLoaded'),
      types.literal('Loading'),
      types.literal('Loaded'),
      types.literal('Failed'),
    ),
  })
  .actions(self => ({
    setStatus(status: 'NotLoaded' | 'Loading' | 'Loaded' | 'Failed') {
      self.status = status
    },

    add(r: any) {
      self.byId.set(r._id, r)
      self.list.push(r._id)
    },

    remove(id: string) {
      // @ts-ignore
      self.list.remove(id)
      self.byId.delete(id)
    },
  }))

export const ErrorModel = types.model('Error', {
  id: types.identifier(types.string),
  message: types.string,
  created: types.number,
})

const Errors = types
  .model('Errors', {
    byId: types.map(ErrorModel),
    list: types.array(types.string),
  })
  .actions(self => ({
    add({message}: {message: string}) {
      const id = uuid()
      const created = Date.now()
      const error = {message, id, created}

      self.byId.set(id, error)
      self.list.push(id)
    },

    remove(id: string) {
      self.list.remove(id)
      self.byId.delete(id)
    },
  }))

const HiddenColumnsInComparableSalesView = types
  .model('HiddenColumnsInComparableSalesView', {
    byId: types.map(types.boolean),
  })
  .actions(self => ({
    add(id: string) {
      self.byId.set(id, true)
    },
    remove(id: string) {
      self.byId.delete(id)
    },
  }))

const Store = types.model('Store', {
  general: GeneralStore,
  auth: AuthStore,
  projects: ProjectsStore,
  housingCorporations: HousingCorporationsStore,
  researchStates: ResearchStatesStore,
  researchQueryPresets: ResearchQueryPresets,
  errors: Errors,
  hiddenColumnsInComparableSalesView: HiddenColumnsInComparableSalesView,
  users: UsersStore,
  notifications: NotificationStore,
})

export function createStateStore(
  state: typeof Store.SnapshotType = initialState,
) {
  const store = Store.create(state)
  /**
   * I tried to reject all changes to a researchState belonging
   * to a frozen housing corporation by using an MST middleware,
   * but turns out you can't hook into all actions on a certain
   * subtree node and undo them based on how the snapshot has changed.
   * So, while it would've been a cleaner solution, the API currently
   * doesn't support it. You'll find the second solution in the sagas.
   */
  // addMiddleware(store.researchStates, (call, next, abort) => {
  //   if (call.type !== 'action') {
  //     throw new Error('Only action types are supported')
  //   }

  //   console.log(call, next, abort);

  //   next(call)
  // }, false)
  return store
}

type IStoreType = typeof Store.Type
export interface IStore extends IStoreType {}

export const initialState: typeof Store.SnapshotType = {
  general: {
    bootstrapped: false,
  },
  auth: {
    __state: {isLoggedIn: false},
  },
  projects: {
    baseProjectsById: {},
    listOfProjectsInProjectsPage: [],
  },
  housingCorporations: {
    list: [],
    byId: {},
  },
  researchStates: {
    byHousingCorporationId: {},
  },
  researchQueryPresets: {
    byId: {},
    list: [],
    status: 'NotLoaded',
  },
  errors: {
    byId: {},
    list: [],
  },
  hiddenColumnsInComparableSalesView: {
    byId: {},
  },
  users: {
    byId: {},
  },
  notifications: {
    byId: {},
    list: [],
    status: 'NotLoaded',
  },
}



// WEBPACK FOOTER //
// ./src/bootstrap/Store.ts